import { AdminMessage } from "@sendbird/chat/message";
import { CoreMessageType, SendableMessageType } from "@sendbird/uikit-react/types/utils";
import { MessageCustomTypes } from "../../../appV2/Chat/type";
import { isSameDay } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { SendbirdGroupChannel } from "../chat.types";
import { APP_V2_APP_EVENTS, logError } from "../../../appV2/lib/analytics";

const DEFAULT_SYSTEM_MESSAGE_TYPES = [MessageCustomTypes.SHIFT_BOOKED];

const HIDDEN_MESSAGE_TYPES = [
  MessageCustomTypes.SHIFT_REVIEW,
  MessageCustomTypes.INPUT_ETA,
  MessageCustomTypes.INPUT_ETA_SUBMITTED,
  MessageCustomTypes.SHIFT_ACTION,
  MessageCustomTypes.SHIFT_TIME_PROPOSAL_UPDATED,
  MessageCustomTypes.SHIFT_TIME_PROPOSAL_REPLIED,
  MessageCustomTypes.SHIFT_INVITE_RESPONSE,
  MessageCustomTypes.FCM_APPROVAL,
  MessageCustomTypes.FCM_REJECT,
];

export function shouldRenderDefaultSystemMessage(message: CoreMessageType): boolean {
  return DEFAULT_SYSTEM_MESSAGE_TYPES.includes(message.customType as MessageCustomTypes);
}

export function isAdminMessage(message: CoreMessageType): message is AdminMessage {
  return message.messageType === "admin";
}

export function isSendableMessage(message: CoreMessageType): message is SendableMessageType {
  return message.messageType === "user" || message.messageType === "file";
}

export function hideMessage(message: CoreMessageType): boolean {
  return HIDDEN_MESSAGE_TYPES.includes(message.customType as MessageCustomTypes);
}

export function isShiftInviteRequestMessage(message: CoreMessageType): message is AdminMessage {
  return isAdminMessage(message) && message.customType === MessageCustomTypes.SHIFT_INVITE_REQUEST;
}

export function isGroupShiftInviteMessage(message: CoreMessageType): message is AdminMessage {
  return message.customType === MessageCustomTypes.GROUP_SHIFT_INVITE_REQUEST;
}

export function isVisitInviteRequestMessage(message: CoreMessageType): message is AdminMessage {
  return isAdminMessage(message) && message.customType === MessageCustomTypes.VISIT_INVITE_REQUEST;
}

export function isFacilityCanceledMeRequestMessage(
  message: CoreMessageType
): message is AdminMessage {
  return message.customType === MessageCustomTypes.FCM_REQUEST;
}

export function isShiftTimeProposalMessage(message: CoreMessageType): message is AdminMessage {
  return (
    isAdminMessage(message) && message.customType === MessageCustomTypes.SHIFT_TIME_PROPOSAL_CREATED
  );
}

export function isWorkerLockNotificationMessage(message: CoreMessageType): message is AdminMessage {
  return (
    isAdminMessage(message) && message.customType === MessageCustomTypes.WORKER_LOCK_NOTIFICATION
  );
}

interface ShowDateSeparatorParams {
  readonly message: CoreMessageType;
  readonly allMessages: readonly CoreMessageType[];
  readonly timeZone: string;
}

export function showDateSeparator({
  message,
  allMessages,
  timeZone,
}: ShowDateSeparatorParams): boolean {
  if (hideMessage(message)) {
    return false;
  }

  const messageIndex = allMessages.findIndex(
    (currentMessage) => currentMessage.messageId === message.messageId
  );

  if (messageIndex === -1) {
    return false;
  }

  const messageCreatedAt = new Date(message.createdAt);

  for (let i = messageIndex - 1; i >= 0; i--) {
    const prevMessage = allMessages[i];
    const prevMessageCreatedAt = new Date(prevMessage.createdAt);

    if (!isSameDay(prevMessageCreatedAt, messageCreatedAt, { timeZone })) {
      return true;
    }

    if (!hideMessage(prevMessage)) {
      return false;
    }
  }

  return true;
}

/**
 * Type guard to validate that a channel has a valid URL before rendering.
 * Prevents runtime errors when channel.url is undefined.
 */
export function hasChannelUrl(channel: SendbirdGroupChannel): boolean {
  const hasUrl = isDefined(channel?.url);

  if (!hasUrl) {
    logError(APP_V2_APP_EVENTS.CHANNEL_PREVIEW_MISSING_URL, {
      error: new Error("channel.url is undefined"),
      metadata: { channel },
    });
  }

  return hasUrl;
}
